import type { UserUuid } from "../identity";
/**
 * @file Types for the admin HI-alert review surface (authentication
 *   `/admin/hi-alerts`).
 * @module types/admin/hiAlerts
 *
 * B1.3H-3R-3 — first thin operator UI slice for `HI_015_005818` amended-search
 * handling after a DVA IHI revalidation no-match outcome (UC.015 Part C).
 *
 * B3.3 (CU-14ygtkj8z2x) — widened to the three category-safe projections the
 * shared surface serves, as a discriminated union on the backend `type` string.
 * Every field declared here is a category-only label, lifecycle metadata or an
 * internal integer id: the backend never returns a raw/masked/partial IHI, a raw
 * DVA file number, demographics, search criteria, the request/response body or a
 * match count, and this UI never displays, persists or infers any of those.
 */

/** HI_010_005839 — an IHI already assigned to another local patient record. */
export const IHI_CONFLICT_TYPE = "ihi-conflict";

/** HI_010_005875 — returned-IHI search criteria matched another patient. */
export const IHI_DEMOGRAPHIC_CONFLICT_TYPE = "ihi-demographic-conflict";

/** The same-patient revalidation review type carried by the first slice. */
export const IHI_REVALIDATION_REVIEW_TYPE = "ihi-revalidation-review";

export type HiAlertReviewType =
	| typeof IHI_CONFLICT_TYPE
	| typeof IHI_DEMOGRAPHIC_CONFLICT_TYPE
	| typeof IHI_REVALIDATION_REVIEW_TYPE;

/** Fixed category review reason that gates the UC.015 amended-search action. */
export const REVIEW_REASON_NO_MATCH = "no_match";

/**
 * CU-14ygtkj8z3m (HI_010_005818) — the UC.010 registration no-match reasons. Each
 * pins the originating supported registration Search Type; the type is never read
 * from form contents or sent back to the backend.
 */
export const REVIEW_REASON_REGISTRATION_NO_MATCH_TYPE_3 = "registration_no_match_type_3";
export const REVIEW_REASON_REGISTRATION_NO_MATCH_TYPE_4 = "registration_no_match_type_4";
export const REVIEW_REASON_REGISTRATION_NO_MATCH_TYPE_5 = "registration_no_match_type_5";
/** CU-14ygtkj98p8 (HI_015_016813): the alert for a Resolved replacement candidate that was not accepted. */
export const REVIEW_REASON_RESOLVED_IHI_REPLACEMENT_NOT_ACCEPTED = "resolved_ihi_replacement_not_accepted";

/** CU-14ygtkj9tu1: the UC.035 patient-merge reasons healthcare raises on the surviving record; severity stays backend-projected. */
export const REVIEW_REASON_MERGE_ALERT_UNSUPPORTED_PROVISIONAL = "merge_alert_unsupported_provisional_record_status";
export const REVIEW_REASON_MERGE_ALERT_UNSUPPORTED_UNVERIFIED = "merge_alert_unsupported_unverified_record_status";
export const REVIEW_REASON_MERGE_ALERT_VERIFIED_RETURNED_UNVERIFIED = "merge_alert_verified_ihi_returned_unverified";
export const REVIEW_REASON_MERGE_STORED_RETIRED_LOCAL_ACTIVE = "merge_stored_retired_warning_local_active";
export const REVIEW_REASON_MERGE_STORED_RETIRED_LOCAL_DECEASED = "merge_stored_retired_warning_local_deceased";
export const REVIEW_REASON_MERGE_WARNING_LOCAL_ACTIVE_RETURNED_DECEASED = "merge_status_warning_local_active_returned_deceased";
export const REVIEW_REASON_MERGE_ALERT_LOCAL_ACTIVE_RETURNED_EXPIRED = "merge_status_transition_alert_local_active_returned_expired";
export const REVIEW_REASON_MERGE_ALERT_LOCAL_DECEASED_RETURNED_ACTIVE = "merge_status_transition_alert_local_deceased_returned_active";
export const REVIEW_REASON_MERGE_ALERT_LOCAL_DECEASED_RETURNED_EXPIRED = "merge_status_transition_alert_local_deceased_returned_expired";
export const REVIEW_REASON_MERGE_RESOLVED_REPLACEMENT_NOT_ACCEPTED = "merge_resolved_ihi_replacement_not_accepted";

/** The requirement anchor the backend projects on every registration no-match review. */
export const REGISTRATION_NO_MATCH_REQUIREMENT_ID = "HI_010_005818";

/** CU-14ygtkj98pb: the UC.015 Medicare and address no-match reasons; each pins its originating Search Type with its anchor. */
export const REVIEW_REASON_MEDICARE_REVALIDATION_NO_MATCH_TYPE_1 = "medicare_revalidation_no_match_type_1";
export const REVIEW_REASON_MEDICARE_REVALIDATION_NO_MATCH_TYPE_2 = "medicare_revalidation_no_match_type_2";
export const REVIEW_REASON_MEDICARE_REVALIDATION_NO_MATCH_TYPE_3 = "medicare_revalidation_no_match_type_3";
export const REVIEW_REASON_ADDRESS_REVALIDATION_NO_MATCH_TYPE_6 = "address_revalidation_no_match_type_6";
export const MEDICARE_REVALIDATION_NO_MATCH_REQUIREMENT_ID = "HI_015_022000";
export const ADDRESS_REVALIDATION_NO_MATCH_REQUIREMENT_ID = "HI_015_005815";

/** Which amended-search contract an open review takes. Derived from the stored reason (and, except for DVA, the projected requirement anchor) only. */
export const AMEND_VARIANT_UC015_DVA_REVALIDATION = "uc015_dva_revalidation";
export const AMEND_VARIANT_UC015_MEDICARE_TYPE_1 = "uc015_medicare_type_1";
export const AMEND_VARIANT_UC015_MEDICARE_TYPE_2 = "uc015_medicare_type_2";
export const AMEND_VARIANT_UC015_MEDICARE_TYPE_3 = "uc015_medicare_type_3";
export const AMEND_VARIANT_UC015_ADDRESS_TYPE_6 = "uc015_address_type_6";
export const AMEND_VARIANT_UC010_REGISTRATION_TYPE_3 = "uc010_registration_type_3";
export const AMEND_VARIANT_UC010_REGISTRATION_TYPE_4 = "uc010_registration_type_4";
export const AMEND_VARIANT_UC010_REGISTRATION_TYPE_5 = "uc010_registration_type_5";

export type RegistrationAmendVariant =
	| typeof AMEND_VARIANT_UC010_REGISTRATION_TYPE_3
	| typeof AMEND_VARIANT_UC010_REGISTRATION_TYPE_4
	| typeof AMEND_VARIANT_UC010_REGISTRATION_TYPE_5;

export type RevalidationAmendVariant =
	| typeof AMEND_VARIANT_UC015_MEDICARE_TYPE_1
	| typeof AMEND_VARIANT_UC015_MEDICARE_TYPE_2
	| typeof AMEND_VARIANT_UC015_MEDICARE_TYPE_3
	| typeof AMEND_VARIANT_UC015_ADDRESS_TYPE_6;

export type AmendableReviewVariant = typeof AMEND_VARIANT_UC015_DVA_REVALIDATION | RevalidationAmendVariant | RegistrationAmendVariant;

export type HiAlertStatus = "open" | "resolved";

/**
 * B1.3H-3W-5D — the bounded severity categories the authentication projection is
 * known to emit for this review type. Severity is derived in the backend from the
 * persisted bounded review reason (`authentication#128`); it is NOT a stored
 * column and it is NOT re-derived here. These constants exist only so the display
 * contract can name the categories it recognises.
 */
export const REVIEW_SEVERITY_WARNING = "warning";
export const REVIEW_SEVERITY_ALERT = "alert";
export const REVIEW_SEVERITY_UNDETERMINED = "undetermined";

/** The severity categories this frontend knows how to present. */
export type KnownReviewSeverity =
	| typeof REVIEW_SEVERITY_WARNING
	| typeof REVIEW_SEVERITY_ALERT
	| typeof REVIEW_SEVERITY_UNDETERMINED;

/** Lifecycle and category fields every surface type projects. */
interface HiAlertReviewBase {
	id: number;
	requirement_id: string;
	status: HiAlertStatus;
	raised_by_user_id: UserUuid | null;
	resolved_by_user_id: UserUuid | null;
	detected_at: string | null;
	resolved_at: string | null;
	created_at: string | null;
	updated_at: string | null;
}

/**
 * Mirrors `PatientIhiAlertReviewService::projectConflictAlert()` — no raw IHI,
 * free-text message or assignment source is projected (CU-14ygtkj8z2x).
 */
export interface IhiConflictAlert extends HiAlertReviewBase {
	type: typeof IHI_CONFLICT_TYPE;
	alert_type: string;
	assigned_user_id: UserUuid;
	conflicting_user_id: UserUuid;
}

/**
 * Mirrors `PatientIhiAlertReviewService::projectDemographicAlert()` — no raw IHI,
 * search criteria, demographics or free-text message is projected (CU-14ygtkj8z2x).
 */
export interface IhiDemographicConflictAlert extends HiAlertReviewBase {
	type: typeof IHI_DEMOGRAPHIC_CONFLICT_TYPE;
	decision: string | null;
	registration_source: string | null;
	search_type: string | null;
	number_status: string | null;
	record_status: string | null;
	assigned_user_id: UserUuid;
	matching_user_id: UserUuid;
}

/**
 * Category-only projection of a same-patient IHI revalidation review. Mirrors the
 * authentication `PatientIhiRevalidationReviewService::projectReview()` shape —
 * fixed requirement/reason constants, allow-listed classifier status labels,
 * lifecycle metadata and internal integer ids only.
 */
export interface IhiRevalidationReview extends HiAlertReviewBase {
	type: typeof IHI_REVALIDATION_REVIEW_TYPE;
	review_reason: string;
	number_status: string | null;
	record_status: string | null;
	ihi_type: string | null;
	/**
	 * The backend-projected severity category. Deliberately typed as a loose string
	 * rather than `KnownReviewSeverity` so that a missing, null, empty or future
	 * backend value is a normal runtime case rather than a type violation that
	 * would tempt a caller into a cast or a local re-classification. Narrow it for
	 * display through `reviewSeverityPresentation()` — never branch on it directly.
	 */
	severity?: string | null;
	patient_user_id: UserUuid;
}

/** One row of the shared operator surface, discriminated on the backend `type`. */
export type HiAlertReview = IhiConflictAlert | IhiDemographicConflictAlert | IhiRevalidationReview;

/** Mixed-feed identity: ids are per backing store, so only type + id is unique (CU-14ygtkj8z2x). */
export interface HiAlertReviewRef {
	type: HiAlertReviewType;
	id: number;
}

export const hiAlertReviewKey = (ref: HiAlertReviewRef): string => `${ref.type}:${ref.id}`;

export interface HiAlertPagination {
	current_page: number;
	per_page: number;
	total_items: number;
	total_pages: number;
}

export interface HiAlertListResponse {
	data: HiAlertReview[];
	pagination: HiAlertPagination;
}

/**
 * The bounded amended-search criteria. The backend accepts ONLY these five keys;
 * they are operator-entered transient input and must never be persisted, logged,
 * placed in a URL or transported anywhere except the submit request body.
 */
export interface AmendedSearchCriteria {
	family_name: string;
	given_name: string;
	/** ISO date, `YYYY-MM-DD`. */
	date_of_birth: string;
	sex: string;
	dva_file_number: string;
}

/** Category-only result returned by the amended-search action. */
export interface AmendedSearchResult {
	originating_review_id: number;
	review_status: string;
	/** A `DvaIhiRevalidationResultClassifier` category outcome label. */
	outcome: string;
	/**
	 * A `PatientIhiRevalidationAssignmentService` category label, or `null` — the
	 * backend only routes an assignment (and returns a label) when `outcome` is
	 * `assignable_single_match`; every other outcome leaves this null.
	 */
	assignment_outcome: string | null;
}

/** CU-14ygtkj8z3m — Search Type 3 (Medicare) amended criteria; exactly the backend allow-list. */
export interface RegistrationType3AmendedCriteria {
	medicare_card_number: string;
	given_name: string;
	family_name: string;
	/** ISO date, `YYYY-MM-DD`. */
	date_of_birth: string;
	sex: string;
}

/** CU-14ygtkj8z3m — Search Type 4 (DVA, two names) amended criteria; exactly the backend allow-list. */
export interface RegistrationType4AmendedCriteria {
	dva_file_number: string;
	given_name: string;
	family_name: string;
	/** ISO date, `YYYY-MM-DD`. */
	date_of_birth: string;
	sex: string;
}

/** CU-14ygtkj8z3m — Search Type 5 (DVA, single name) amended criteria; no given_name key exists. */
export interface RegistrationType5AmendedCriteria {
	dva_file_number: string;
	family_name: string;
	/** ISO date, `YYYY-MM-DD`. */
	date_of_birth: string;
	sex: string;
}

/**
 * The registration resubmission request: the variant (from the stored review
 * reason) pins which criteria set is valid, so a caller can never hand the client
 * another type's identifier or a type selector.
 */
export type RegistrationAmendedSearchRequest =
	| { variant: typeof AMEND_VARIANT_UC010_REGISTRATION_TYPE_3; criteria: RegistrationType3AmendedCriteria }
	| { variant: typeof AMEND_VARIANT_UC010_REGISTRATION_TYPE_4; criteria: RegistrationType4AmendedCriteria }
	| { variant: typeof AMEND_VARIANT_UC010_REGISTRATION_TYPE_5; criteria: RegistrationType5AmendedCriteria };

/**
 * Category-only result of the UC.010 registration resubmission. Distinct from
 * {@link AmendedSearchResult}: `assignment` is the registration writer's bounded
 * label (`assigned` / `not_assigned` / `failed`), not the UC.015 `assignment_outcome`.
 */
export interface RegistrationAmendedSearchResult {
	originating_review_id: number;
	review_status: string;
	requirement_id: string;
	search_type: string;
	search_type_number: number;
	/** `single_match` / `no_match` / `multiple_match` / `indeterminate`, or a future label. */
	outcome: string;
	/** `assigned` / `not_assigned` / `failed`, or a future label. */
	assignment: string;
}

/** CU-14ygtkj98pb — Medicare Search Type 1 amended criteria; exactly the backend allow-list, ISO date of birth. */
export interface RevalidationMedicareType1AmendedCriteria {
	medicare_card_number: string;
	individual_reference_number: string;
	given_name: string;
	family_name: string;
	date_of_birth: string;
	sex: string;
}

/** CU-14ygtkj98pb — Medicare Search Type 2 (single name) amended criteria; no given_name key exists. */
export interface RevalidationMedicareType2AmendedCriteria {
	medicare_card_number: string;
	individual_reference_number: string;
	family_name: string;
	date_of_birth: string;
	sex: string;
}

/** CU-14ygtkj98pb — Medicare Search Type 3 amended criteria; no IRN key exists. */
export interface RevalidationMedicareType3AmendedCriteria {
	medicare_card_number: string;
	given_name: string;
	family_name: string;
	date_of_birth: string;
	sex: string;
}

/** CU-14ygtkj98pb — address Search Type 6 amended criteria; no Medicare, IRN or DVA key exists. */
export interface RevalidationAddressType6AmendedCriteria {
	given_name: string;
	family_name: string;
	date_of_birth: string;
	sex: string;
	address: string;
}

/** CU-14ygtkj98pb: the variant (from the stored review contract) pins the criteria set; no type selector exists. */
export type RevalidationAmendedSearchRequest =
	| { variant: typeof AMEND_VARIANT_UC015_MEDICARE_TYPE_1; criteria: RevalidationMedicareType1AmendedCriteria }
	| { variant: typeof AMEND_VARIANT_UC015_MEDICARE_TYPE_2; criteria: RevalidationMedicareType2AmendedCriteria }
	| { variant: typeof AMEND_VARIANT_UC015_MEDICARE_TYPE_3; criteria: RevalidationMedicareType3AmendedCriteria }
	| { variant: typeof AMEND_VARIANT_UC015_ADDRESS_TYPE_6; criteria: RevalidationAddressType6AmendedCriteria };

/** CU-14ygtkj98pb: category-only result; relation and transition are bounded labels or null. */
export interface RevalidationAmendedSearchResult {
	originating_review_id: number;
	review_status: string;
	requirement_id: string;
	search_type_number: number;
	current_ihi_relation: string | null;
	transition_action: string | null;
	outcome: string;
}

export const isIhiRevalidationReview = (review: HiAlertReview): review is IhiRevalidationReview =>
	review.type === IHI_REVALIDATION_REVIEW_TYPE;

/**
 * CU-14ygtkj8z3m / CU-14ygtkj98pb — which amended-search contract an open review takes, or null when it is not amendable.
 * Only a revalidation-type review qualifies, and every reason except the DVA `no_match` also needs its exact anchor.
 */
export const amendableReviewVariant = (review: HiAlertReview): AmendableReviewVariant | null => {
	if (!isIhiRevalidationReview(review) || review.status !== "open") {
		return null;
	}
	if (review.review_reason === REVIEW_REASON_NO_MATCH) {
		return AMEND_VARIANT_UC015_DVA_REVALIDATION;
	}
	if (review.requirement_id === MEDICARE_REVALIDATION_NO_MATCH_REQUIREMENT_ID) {
		switch (review.review_reason) {
			case REVIEW_REASON_MEDICARE_REVALIDATION_NO_MATCH_TYPE_1:
				return AMEND_VARIANT_UC015_MEDICARE_TYPE_1;
			case REVIEW_REASON_MEDICARE_REVALIDATION_NO_MATCH_TYPE_2:
				return AMEND_VARIANT_UC015_MEDICARE_TYPE_2;
			case REVIEW_REASON_MEDICARE_REVALIDATION_NO_MATCH_TYPE_3:
				return AMEND_VARIANT_UC015_MEDICARE_TYPE_3;
			default:
				return null;
		}
	}
	if (review.requirement_id === ADDRESS_REVALIDATION_NO_MATCH_REQUIREMENT_ID) {
		return review.review_reason === REVIEW_REASON_ADDRESS_REVALIDATION_NO_MATCH_TYPE_6 ? AMEND_VARIANT_UC015_ADDRESS_TYPE_6 : null;
	}
	if (review.requirement_id !== REGISTRATION_NO_MATCH_REQUIREMENT_ID) {
		return null;
	}
	switch (review.review_reason) {
		case REVIEW_REASON_REGISTRATION_NO_MATCH_TYPE_3:
			return AMEND_VARIANT_UC010_REGISTRATION_TYPE_3;
		case REVIEW_REASON_REGISTRATION_NO_MATCH_TYPE_4:
			return AMEND_VARIANT_UC010_REGISTRATION_TYPE_4;
		case REVIEW_REASON_REGISTRATION_NO_MATCH_TYPE_5:
			return AMEND_VARIANT_UC010_REGISTRATION_TYPE_5;
		default:
			return null;
	}
};

/**
 * Amend is offered only for an open amendable revalidation review; narrowing on the
 * discriminator first means a cross-patient alert can never qualify (CU-14ygtkj8z2x),
 * then only the amendable no-match contracts pass (CU-14ygtkj8z3m, CU-14ygtkj98pb). UI-side guard
 * only — the backend re-checks and answers 404/409 otherwise.
 */
export const canAmendReview = (review: HiAlertReview): review is IhiRevalidationReview =>
	isIhiRevalidationReview(review) && amendableReviewVariant(review) !== null;

/** The numeric Search Type a registration variant pins, for presentation selection only. */
export const registrationSearchTypeNumber = (variant: RegistrationAmendVariant): 3 | 4 | 5 => {
	switch (variant) {
		case AMEND_VARIANT_UC010_REGISTRATION_TYPE_3:
			return 3;
		case AMEND_VARIANT_UC010_REGISTRATION_TYPE_4:
			return 4;
		case AMEND_VARIANT_UC010_REGISTRATION_TYPE_5:
			return 5;
	}
};

/**
 * The operator resolve control is offered only for a review that is still open.
 * Unlike the amended-search control it is not gated on type or reason — an
 * approved operator may resolve any open review. The backend remains authoritative
 * (it re-checks the status and answers 409 for a non-open review); this is a
 * UI-side guard only.
 */
export const canResolveReview = (review: Pick<HiAlertReview, "status">): boolean =>
	review.status === "open";
